import { createRouter, createWebHistory } from 'vue-router'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import { adminBaseRoute, staticRoutes } from '/@/router/static'
import { loading } from '/@/utils/loading'
import langAutoLoadMap from '/@/lang/autoload'
import { mergeMessage } from '/@/lang'
import { useConfig } from '/@/stores/config'
import { isAdminApp } from '/@/utils/common'
import { uniq } from 'lodash-es'
import { useAdminInfo } from '/@/stores/adminInfo'
import { usePermission } from '/@/stores/permission'
import { initUserPermissionLoading } from '/@/hooks/useUserHooks'
import { errorHandler } from '/@/utils/errorUtils'

NProgress.configure({ showSpinner: false })

const routeWhiteList = [ '/login', '/demo', '/401', '/404' ]

const router = createRouter({
  history: createWebHistory(),
  routes: staticRoutes
})

router.beforeEach((to, from, next) => {
  NProgress.start()
  if (!window.existLoading) {
    loading.show()
    window.existLoading = true
  }

  const adminInfo = useAdminInfo()
  const permissionStore = usePermission()

  /* 进入login页必须清掉token和清除permissionStore isLoad标志，防止特殊操作登录后无法请求最新的权限
   *
   * 特殊操作登录：已登录状态下的重新登录
   * 场景：
   *  1. 用户A已登录（token存在，permissionStore.isLoad = true）
   *  2. 用户A访问 /login?oldLinkedToken=xxx 或手动进入登录页
   * 如果不清除token和isLoad标志，路由守卫会：
   *  1. 检测到有token
   *  2. 检测到 isLoad = true
   *  3. 直接 next()，不会重新初始化权限
   *  4. 导致新登录用户使用了旧账号的权限数据
   */
  // if (to.name === 'adminLogin') {
  //   adminInfo.removeToken()
  //   permissionStore.resetIsLoad()
  // }

  // 按需动态加载页面的语言包-start
  let loadPath: string[] = []
  const config = useConfig()
  if (to.path in langAutoLoadMap) {
    loadPath.push(...langAutoLoadMap[to.path as keyof typeof langAutoLoadMap])
  }
  let prefix = ''

  if (isAdminApp(to.fullPath)) {
    prefix = './' + config.lang.defaultLang

    // 去除 path 中的 /admin
    const adminPath = to.path.slice(to.path.indexOf(adminBaseRoute.path) + adminBaseRoute.path.length)
    if (adminPath) loadPath.push(prefix + adminPath + '.ts')
  }

  // 根据路由 name 加载的语言包
  if (to.name) {
    loadPath.push(prefix + '/' + to.name.toString() + '.ts')
  }

  if (!window.loadLangHandle.publicMessageLoaded) window.loadLangHandle.publicMessageLoaded = []
  const publicMessagePath = prefix + '.ts'
  if (!window.loadLangHandle.publicMessageLoaded.includes(publicMessagePath)) {
    loadPath.push(publicMessagePath)
    window.loadLangHandle.publicMessageLoaded.push(publicMessagePath)
  }

  // 去重
  loadPath = uniq(loadPath)

  for (const key in loadPath) {
    loadPath[key] = loadPath[key].replaceAll('${lang}', config.lang.defaultLang)
    if (loadPath[key] in window.loadLangHandle) {
      window.loadLangHandle[loadPath[key]]().then((res: { default: anyObj }) => {
        const pathName = loadPath[key].slice(
          loadPath[key].lastIndexOf(prefix) + (prefix.length + 1),
          loadPath[key].lastIndexOf('.')
        )
        mergeMessage(res.default, pathName)
      })
    }
  }
  // 动态加载语言包-end

  // 没有登录
  if (!adminInfo.getToken()) {
    if (routeWhiteList.indexOf(to.path) !== -1) {
      // 在免登录白名单，直接进入
      next()
    } else {
      // 否则全部重定向到登录页
      location.href = '/login'
    }
    return
  }

  // 获取权限
  if (permissionStore.getIsLoad()) {
    next()
  } else {
    initUserPermissionLoading.value = true
    permissionStore
      .initPermission()
      .then(() => {
        next()
      })
      .catch(() => {
        adminInfo.removeToken().then(() => {
          window.tokenExpiresVisible = false
          location.href = '/login'
        })
      })
      .finally(() => {
        initUserPermissionLoading.value = false
      })
  }
})

// 路由加载后
router.afterEach(() => {
  if (window.existLoading) {
    loading.hide()
  }
  NProgress.done()
})

router.onError(error => {
  errorHandler(error)
})

export default router
